// src/components/Landing.tsx
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useState, useEffect, useRef } from 'react'
import { 
  Sparkles, Zap, Shield, Globe, 
  BarChart3, Mail, ChevronRight, Check, Star,
  ArrowRight, Gift, Facebook, Twitter, Linkedin, Instagram,
  Menu, X, Play, Pause, Volume2, VolumeX, Copy, Send,
  Target, Users, Link2, MessageCircle
} from 'lucide-react'
import './Landing.css'

const PROMO_CODE = 'LAUNCH2026'
const PROMO_DISCOUNT = 10
const PROMO_FREE_MONTHS = 3
const PRICING_PLANS = [
  {
    id: 'starter', name: 'Starter', description: 'For individuals getting started',
    monthlyPrice: 29, yearlyPrice: 21,
    features: ['AI Email Generation - Unlimited','All 541 Niche Profiles','Compliance Tools','Community Access','Share Links - Unlimited','1,000 Subscribers','5 Templates','1 Team Member','Email Tracking - 5/mo','Smart Brief - 10/mo','Basic Analytics'],
    highlightedFeatures: ['AI subject line generator','Mobile responsive templates'],
    popular: false
  },
  {
    id: 'professional', name: 'Professional', description: 'For growing businesses',
    monthlyPrice: 99, yearlyPrice: 79,
    features: ['Everything in Starter','10,000 Subscribers','20 Templates','5 Team Members','Email Tracking - 200/mo','Smart Brief - 30/mo','Behavioral Learning - Advanced','API Access - 20/mo','Advanced Analytics','Priority Support'],
    highlightedFeatures: ['Behavioral Learning','API Access','Advanced Analytics'],
    popular: true, badge: 'Most Popular'
  },
  {
    id: 'enterprise', name: 'Enterprise', description: 'For large organizations',
    monthlyPrice: 299, yearlyPrice: 249,
    features: ['Everything in Pro','50,000+ Subscribers','Unlimited Templates','Unlimited Team Members','Email Tracking - Unlimited','Smart Brief - 50/mo','Behavioral Learning - Full','API Access - 50/mo'],
    highlightedFeatures: ['Unlimited Everything','Full Behavioral Learning','50/mo API Calls'],
    popular: false, badge: 'Enterprise'
  }
]

const FAQS = [
  { question: 'How does the AI email generation work?', answer: 'Our AI analyzes your brief, target audience, and campaign goals to generate personalized email content. It learns from millions of successful campaigns to optimize for engagement and conversions.' },
  { question: 'Can I cancel anytime?', answer: 'Yes! You can cancel your subscription at any time. No long-term contracts required.' },
  { question: 'Is my data secure?', answer: 'We take security seriously. All data is encrypted at rest and in transit. We\'re SOC 2 Type II certified and GDPR compliant.' },
  { question: 'How does the promo code LAUNCH2026 work?', answer: `Use code LAUNCH2026 at signup to get ${PROMO_DISCOUNT}% off your first ${PROMO_FREE_MONTHS} months free! New users only.` }
]

const TRUSTED_BY = [
  { name: 'TechCorp', logo: '🏢' },
  { name: 'GrowthLabs', logo: '📈' },
  { name: 'CreativeStudio', logo: '🎨' },
  { name: 'DataFlow', logo: '📊' },
  { name: 'CloudScale', logo: '☁️' }
]

export default function Landing() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [copiedCode, setCopiedCode] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)
  const [appliedPromo, setAppliedPromo] = useState(false)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
 const REFERRAL_REWARD = 10
  useEffect(() => {
    const urlPromo = searchParams.get('promo')
    if (urlPromo) {
      localStorage.setItem('appliedPromo', JSON.stringify({
        code: urlPromo, discountPercent: PROMO_DISCOUNT,
        freeMonths: PROMO_FREE_MONTHS, appliedAt: new Date().toISOString(), source: 'direct_link'
      }))
      setAppliedPromo(true)
    }
    if (localStorage.getItem('appliedPromo')) setAppliedPromo(true)
  }, [searchParams])
  
  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight
      setScrollProgress((winScroll / height) * 100)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => { entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('visible') }) },
      { threshold: 0.15, rootMargin: '50px' }
    )
    document.querySelectorAll('.animate-on-scroll').forEach(el => observerRef.current?.observe(el))
    return () => observerRef.current?.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    let particles: Array<{x:number;y:number;vx:number;vy:number;size:number;opacity:number}> = []
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; initParticles() }
    const initParticles = () => {
      particles = []
      for (let i = 0; i < Math.floor((canvas.width * canvas.height) / 15000); i++) {
        particles.push({ x: Math.random()*canvas.width, y: Math.random()*canvas.height, vx: (Math.random()-0.5)*0.2, vy: (Math.random()-0.5)*0.2, size: Math.random()*2+1, opacity: Math.random()*0.3+0.1 })
      }
    }
    const animate = () => {
      if (!ctx || !canvas) return
      ctx.clearRect(0,0,canvas.width,canvas.height)
      particles.forEach(p => { p.x+=p.vx; p.y+=p.vy; if(p.x<0||p.x>canvas.width)p.vx*=-1; if(p.y<0||p.y>canvas.height)p.vy*=-1; ctx.beginPath(); ctx.arc(p.x,p.y,p.size,0,Math.PI*2); ctx.fillStyle=`rgba(37,99,235,${p.opacity})`; ctx.fill() })
      ctx.strokeStyle='rgba(37,99,235,0.05)'; ctx.lineWidth=0.5
      particles.forEach((p1,i) => { particles.slice(i+1).forEach(p2 => { const d=Math.hypot(p1.x-p2.x,p1.y-p2.y); if(d<100){ctx.beginPath();ctx.moveTo(p1.x,p1.y);ctx.lineTo(p2.x,p2.y);ctx.stroke()} }) })
      requestAnimationFrame(animate)
    }
    resize(); window.addEventListener('resize',resize); animate()
    return () => window.removeEventListener('resize',resize)
  }, [])

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) { const offset=80; const pos=el.getBoundingClientRect().top+window.pageYOffset-offset; window.scrollTo({top:pos,behavior:'smooth'}) }
    setIsMenuOpen(false)
  }

  const handleVideoToggle = () => {
    if (videoRef.current) {
      if (isVideoPlaying) { videoRef.current.pause() } else { videoRef.current.play() }
      setIsVideoPlaying(!isVideoPlaying)
    }
  }

  const handleCopyPromo = () => { navigator.clipboard.writeText(PROMO_CODE); setCopiedCode(true); setTimeout(()=>setCopiedCode(false),2000) }
  
  const handleApplyPromo = () => {
    localStorage.setItem('appliedPromo', JSON.stringify({ code:PROMO_CODE, discountPercent:PROMO_DISCOUNT, freeMonths:PROMO_FREE_MONTHS, appliedAt:new Date().toISOString(), source:'landing_page' }))
    setAppliedPromo(true); navigate(`/signup?promo=${PROMO_CODE}`)
  }
  
  const generateReferralLink = () => `${window.location.origin}/signup?promo=${user?.id ? `REF-${user.id}` : PROMO_CODE}`
  
  const handleCopyReferralLink = () => { navigator.clipboard.writeText(generateReferralLink()); setCopiedLink(true); setTimeout(()=>setCopiedLink(false),2000) }
  
  const handleShare = (platform: string) => {
    const link = generateReferralLink()
    const text = encodeURIComponent(`🚀 Get ${PROMO_DISCOUNT}% off EmailBrain for ${PROMO_FREE_MONTHS} months free!`)
    const url = encodeURIComponent(link)
    const urls: Record<string,string> = { twitter:`https://twitter.com/intent/tweet?text=${text}&url=${url}`, facebook:`https://www.facebook.com/sharer/sharer.php?u=${url}`, linkedin:`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, whatsapp:`https://wa.me/?text=${text}%20${url}` }
    if (urls[platform]) window.open(urls[platform],'_blank','width=600,height=400')
  }

  const getPrice = (plan: typeof PRICING_PLANS[0]) => {
    const base = billingCycle==='monthly' ? plan.monthlyPrice : plan.yearlyPrice
    return appliedPromo ? Math.round(base*(1-PROMO_DISCOUNT/100)) : base
  }

    return (
    <div className="landing-container">
      <canvas ref={canvasRef} className="animated-bg" />
      <div className="scroll-progress" style={{width:`${scrollProgress}%`}} />

      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-content">
            <Link to="/" className="logo"><div className="logo-icon"><Zap className="logo-svg"/></div><span className="logo-text">EmailBrain</span></Link>
            <div className="desktop-nav">
              <button onClick={()=>scrollToSection('features')} className="nav-link">Features</button>
              <button onClick={()=>scrollToSection('how-it-works')} className="nav-link">How It Works</button>
              <button onClick={()=>scrollToSection('pricing')} className="nav-link">Pricing</button>
              <button onClick={()=>scrollToSection('faq')} className="nav-link">FAQ</button>
            </div>
            <div className="desktop-auth">
              {localStorage.getItem('token') || localStorage.getItem('accessToken') ? (
                <Link to="/dashboard" className="btn-primary btn-sm">Dashboard <ArrowRight size={16}/></Link>
              ) : (
                <>
                  <Link to="/signin" className="nav-link">Sign In</Link>
                  <Link to={`/signup${appliedPromo?`?promo=${PROMO_CODE}`:''}`} className="btn-primary btn-sm">Get Started <ArrowRight size={16}/></Link>
                </>
              )}
            </div>
            <button className="mobile-menu-btn" onClick={()=>setIsMenuOpen(!isMenuOpen)}>{isMenuOpen?<X size={24}/>:<Menu size={24}/>}</button>
          </div>
          {isMenuOpen && (
            <div className="mobile-menu">
              <button onClick={()=>scrollToSection('features')} className="mobile-link">Features</button>
              <button onClick={()=>scrollToSection('how-it-works')} className="mobile-link">How It Works</button>
              <button onClick={()=>scrollToSection('pricing')} className="mobile-link">Pricing</button>
              <button onClick={()=>scrollToSection('faq')} className="mobile-link">FAQ</button>
              <div className="mobile-auth">
                {localStorage.getItem('token') || localStorage.getItem('accessToken') ? <Link to="/dashboard" className="btn-primary btn-block">Dashboard</Link> : (<><Link to="/signin" className="btn-outline btn-block">Sign In</Link><Link to="/signup" className="btn-primary btn-block">Get Started</Link></>)}
              </div>
            </div>
          )}
        </div>
      </nav>

      {appliedPromo && (<div className="promo-banner"><Gift size={16}/><span>🎉 {PROMO_DISCOUNT}% discount applied! You'll get {PROMO_FREE_MONTHS} months free on any plan.</span></div>)}

      <main>
        <section className="hero-section">
          <div className="hero-container">
            <div className="hero-badge animate-on-scroll"><Sparkles size={16}/><span>Introducing EmailBrain 3.0</span><Sparkles size={16}/></div>
            <h1 className="hero-title animate-on-scroll">Create Email Campaigns That<span className="hero-gradient"> Actually Convert</span></h1>
            <p className="hero-description animate-on-scroll">AI-powered email marketing that learns from your audience, optimizes in real-time, and delivers up to 4x higher engagement.</p>
            <div className="hero-cta animate-on-scroll">
              <button onClick={handleVideoToggle} className="btn-outline btn-large"><Play size={20}/> Watch Demo</button>
            </div>
            {!appliedPromo && (
              <div className="hero-promo-flash animate-on-scroll" onClick={handleApplyPromo}>
                <Gift size={18}/><span>Use code <strong>{PROMO_CODE}</strong> for {PROMO_DISCOUNT}% off + {PROMO_FREE_MONTHS} months free!</span><ArrowRight size={18}/>
              </div>
            )}
            <div className="hero-stats animate-on-scroll">
              <div className="stat"><span className="stat-value">10K+</span><span className="stat-label">Active Users</span></div>
              <div className="stat-divider"/>
              <div className="stat"><span className="stat-value">50M+</span><span className="stat-label">Emails Sent</span></div>
              <div className="stat-divider"/>
              <div className="stat"><span className="stat-value">4.9</span><span className="stat-label">User Rating</span></div>
            </div>
            <div className="trusted-by animate-on-scroll">
              <p className="trusted-label">Trusted by innovative companies</p>
              <div className="trusted-logos">{TRUSTED_BY.map((c,i)=>(<div key={i} className="trusted-logo"><span>{c.logo}</span><span>{c.name}</span></div>))}</div>
            </div>
          </div>
          {isVideoPlaying && (
            <div className="video-modal" onClick={handleVideoToggle}>
              <div className="video-container" onClick={e=>e.stopPropagation()}>
                <video ref={videoRef} src="/demo-video.mp4" className="demo-video" poster="/video-poster.jpg"/>
                <div className="video-controls">
                  <button onClick={handleVideoToggle}><Pause size={20}/></button>
                  <button onClick={()=>{if(videoRef.current){videoRef.current.muted=!isMuted;setIsMuted(!isMuted)}}}>{isMuted?<VolumeX size={20}/>:<Volume2 size={20}/>}</button>
                </div>
                <button className="close-video" onClick={handleVideoToggle}><X size={24}/></button>
              </div>
            </div>
          )}
        </section>

        <section id="features" className="features-section">
          <div className="section-container">
            <div className="section-header animate-on-scroll"><span className="section-badge">Features</span><h2 className="section-title">Everything You Need to<span className="title-highlight"> Scale Your Email Marketing</span></h2><p className="section-description">Powerful AI tools that help you create, optimize, and analyze email campaigns with ease.</p></div>
            <div className="features-grid">
              {[{icon:Sparkles,g:'gradient-1',t:'AI Content Generation',d:'Generate high-converting email copy, subject lines, and CTAs in seconds.',h:['Personalized content','50+ languages','Tone customization']},{icon:Target,g:'gradient-2',t:'Behavioral Targeting',d:'Segment your audience based on behavior and engagement patterns.',h:['Dynamic segmentation','Predictive analytics','Automated workflows']},{icon:BarChart3,g:'gradient-3',t:'Advanced Analytics',d:'Get deep insights with real-time dashboards and reports.',h:['Real-time tracking','ROI calculator','Custom reports']},{icon:Globe,g:'gradient-4',t:'Multi-Channel Integration',d:'Connect with social media, CRM, and analytics tools.',h:['Facebook & TikTok','Salesforce sync','Zapier integration']},{icon:Shield,g:'gradient-5',t:'Enterprise Security',d:'Bank-level security with SSO, 2FA, GDPR and CCPA compliance.',h:['SOC 2 Type II','Data encryption','Audit logs']},{icon:Zap,g:'gradient-6',t:'Automation Studio',d:'Build complex workflows with visual drag-and-drop builder.',h:['Visual builder','100+ triggers','A/B testing']}].map((f,i)=>(
                <div key={i} className="feature-card animate-on-scroll"><div className={`feature-icon ${f.g}`}><f.icon size={28}/></div><h3>{f.t}</h3><p>{f.d}</p><ul className="feature-highlights">{f.h.map((h,j)=><li key={j}><Check size={16}/> {h}</li>)}</ul></div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="how-it-works-section">
          <div className="section-container">
            <div className="section-header animate-on-scroll"><span className="section-badge">Simple Process</span><h2 className="section-title">Launch Your First Campaign in<span className="title-highlight"> Under 5 Minutes</span></h2></div>
            <div className="steps-container">
              <div className="step-item animate-on-scroll"><div className="step-number">01</div><div className="step-icon"><Mail size={32}/></div><h3>Connect Your List</h3><p>Import subscribers from any platform or use our API.</p></div>
              <div className="step-arrow">→</div>
              <div className="step-item animate-on-scroll"><div className="step-number">02</div><div className="step-icon"><Sparkles size={32}/></div><h3>Generate Content</h3><p>Describe your campaign and let AI create content instantly.</p></div>
              <div className="step-arrow">→</div>
              <div className="step-item animate-on-scroll"><div className="step-number">03</div><div className="step-icon"><Send size={32}/></div><h3>Launch & Optimize</h3><p>Send and watch AI optimize for maximum engagement.</p></div>
            </div>
          </div>
        </section>

        <section id="pricing" className="pricing-section">
          <div className="section-container">
            <div className="section-header animate-on-scroll"><span className="section-badge">Pricing</span><h2 className="section-title">Simple, Transparent<span className="title-highlight"> Pricing</span></h2><p className="section-description">Choose the perfect plan for your business. No hidden fees.</p>{appliedPromo && (<div className="pricing-promo-badge"><Gift size={16}/><span>{PROMO_DISCOUNT}% discount applied! First {PROMO_FREE_MONTHS} months free.</span></div>)}</div>
            <div className="billing-toggle animate-on-scroll">
              <button className={`toggle-option ${billingCycle==='monthly'?'active':''}`} onClick={()=>setBillingCycle('monthly')}>Monthly</button>
              <button className={`toggle-option ${billingCycle==='yearly'?'active':''}`} onClick={()=>setBillingCycle('yearly')}>Yearly <span className="save-badge">Save 25-28%</span></button>
            </div>
            <div className="pricing-grid">
              {PRICING_PLANS.map((plan,i)=>{
                const dp = getPrice(plan)
                const op = billingCycle==='monthly'?plan.monthlyPrice:plan.yearlyPrice
                return (
                  <div key={plan.id} className={`pricing-card ${plan.popular?'featured':''} animate-on-scroll`} style={{animationDelay:`${i*0.1}s`}}>
                    {plan.badge && <div className="card-badge">{plan.badge}</div>}
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-description">{plan.description}</p>
                    <div className="plan-price">
                      {appliedPromo && <span className="original-price"><s>${op}</s></span>}
                      <span className="currency">$</span><span className="amount">{dp}</span><span className="period">/month{billingCycle==='yearly'&&', billed annually'}</span>
                    </div>
                    {appliedPromo && <div className="promo-savings-badge">You save ${op-dp}/mo</div>}
                    {billingCycle==='yearly' && <div className="yearly-savings"><span className="savings-highlight">Save {Math.round((1-plan.yearlyPrice/plan.monthlyPrice)*100)}%</span> (${(plan.monthlyPrice*12)-(plan.yearlyPrice*12)}/year)</div>}
                    <ul className="plan-features">{plan.features.map((f,j)=><li key={j}><Check size={18} className="check-icon"/>{f}</li>)}</ul>
                    <div className="plan-highlights"><p className="highlights-title">✨ Includes:</p><ul>{plan.highlightedFeatures.map((f,j)=><li key={j}><Star size={14}/>{f}</li>)}</ul></div>
                  </div>
                )
              })}
            </div>
            <div className="promo-referral-section animate-on-scroll">
              <div className="promo-card">
                <div className="promo-icon"><Gift size={28}/></div>
                <h3>Special Launch Offer</h3>
                <p>New users get {PROMO_DISCOUNT}% off for {PROMO_FREE_MONTHS} months free!</p>
                <div className="promo-code" onClick={handleCopyPromo}><code>{PROMO_CODE}</code><button className="copy-btn">{copiedCode?<Check size={18}/>:<Copy size={18}/>}</button></div>
                <span className="promo-note">{copiedCode?'Copied!':'Click to copy'}</span>
                <button onClick={handleApplyPromo} className="btn-apply-promo"><Gift size={16}/> Apply Discount Now</button>
              </div>
              {localStorage.getItem('token') || localStorage.getItem('accessToken') ? (
                <div className="referral-card">
                  <div className="referral-icon"><Users size={28}/></div>
                  <h3>Refer & Earn</h3><p>Share your link and earn {REFERRAL_REWARD}% when friends subscribe!</p>
                  <div className="referral-link-box"><Link2 size={16}/><input type="text" value={generateReferralLink()} readOnly className="referral-input"/><button onClick={handleCopyReferralLink} className="copy-link-btn">{copiedLink?<Check size={16}/>:<Copy size={16}/>}</button></div>
                  <div className="share-buttons">
                    <button onClick={()=>handleShare('twitter')} className="share-btn twitter"><Twitter size={16}/> Tweet</button>
                    <button onClick={()=>handleShare('facebook')} className="share-btn facebook"><Facebook size={16}/> Share</button>
                    <button onClick={()=>handleShare('linkedin')} className="share-btn linkedin"><Linkedin size={16}/> Post</button>
                    <button onClick={()=>handleShare('whatsapp')} className="share-btn whatsapp"><MessageCircle size={16}/> Send</button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section id="faq" className="faq-section">
          <div className="section-container">
            <div className="section-header animate-on-scroll"><span className="section-badge">FAQ</span><h2 className="section-title">Frequently Asked<span className="title-highlight"> Questions</span></h2></div>
            <div className="faq-grid">
              {FAQS.map((faq,i)=>(
                <div key={i} className={`faq-item ${activeFAQ===i?'active':''}`} onClick={()=>setActiveFAQ(activeFAQ===i?null:i)}>
                  <div className="faq-question"><span>{faq.question}</span><ChevronRight className={`faq-icon ${activeFAQ===i?'rotated':''}`}/></div>
                  {activeFAQ===i && <div className="faq-answer"><p>{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand"><Link to="/" className="footer-logo"><Zap size={24}/><span>EmailBrain</span></Link><p className="footer-tagline">Intelligent email marketing powered by advanced AI.</p><div className="social-links"><a href="https://facebook.com" target="_blank" rel="noopener noreferrer"><Facebook size={20}/></a><a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><Twitter size={20}/></a><a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><Linkedin size={20}/></a><a href="https://instagram.com" target="_blank" rel="noopener noreferrer"><Instagram size={20}/></a></div></div>
            <div className="footer-links"><h4>Product</h4><ul><li><button onClick={()=>scrollToSection('features')}>Features</button></li><li><button onClick={()=>scrollToSection('pricing')}>Pricing</button></li><li><Link to="/integrations">Integrations</Link></li></ul></div>
            <div className="footer-links"><h4>Resources</h4><ul><li><Link to="/blog">Blog</Link></li><li><Link to="/guides">Guides</Link></li><li><Link to="/help">Help Center</Link></li></ul></div>
            <div className="footer-links"><h4>Company</h4><ul><li><Link to="/about">About</Link></li><li><Link to="/careers">Careers</Link></li><li><Link to="/contact">Contact</Link></li></ul></div>
            <div className="footer-links"><h4>Legal</h4><ul><li><Link to="/terms">Terms</Link></li><li><Link to="/privacy">Privacy</Link></li><li><Link to="/security">Security</Link></li></ul></div>
          </div>
          <div className="footer-bottom"><p>&copy; 2026 EmailBrain. All rights reserved.</p></div>
        </div>
      </footer>
    </div>
  )
}